import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { Tariffe_Costo, TipologiaAssociazioni, Tipologia_Uso } from "../prismaTypes";
import { useEffect, useState } from "react";
import { queryClient } from "../App";
import DialogTitle from "@mui/material/DialogTitle";
import TableRow from "@mui/material/TableRow";
import DialogContent from "@mui/material/DialogContent";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { TariffeData } from "../tariffeDefinizioni/TariffeData";

interface Tariffa {
  tipologia_AssociazioniId: number;
  tipologia_UsoId: number;
  costoPerSlot: number | null;
}

interface SaveTariffeCostoDto {
  tariffe_DefinizioniId: number;
  tariffe: Tariffa[];
}

export function TariffeCosto(props: { data: TariffeData; closeDialog: () => void }) {
  const { data, closeDialog } = props;
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading, refetch } = useQuery<{ tariffe: Tariffe_Costo[] }, AxiosError>(
    ["tariffe-costo", data.id],
    () => axios.get(API_URL + "tariffe-costo/findAll/" + data.id).then((res) => res.data),
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setTariffeState(data.tariffe);
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar("Si è verificato un errore!", { variant: "error" });
      },
    }
  );
  const { isLoading: associazioniLoading, data: associazioni } = useQuery<
    unknown,
    AxiosError,
    { tipologie: TipologiaAssociazioni[] }
  >(["tipologie-associazioni/findAll"], () =>
    axios.get(API_URL + "tipologie-associazioni/findAll").then((res) => res.data)
  );

  const { isLoading: isLoadingUso, data: uso } = useQuery<
    unknown,
    AxiosError,
    { tipologieUso: Tipologia_Uso[] }
  >(["tipologie-uso/findAll"], () =>
    axios.get(API_URL + "tipologie-uso/findAll").then((res) => res.data)
  );

  const { mutate } = useMutation(
    (data: SaveTariffeCostoDto) => {
      return axios.post(API_URL + "tariffe-costo/save", data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Tariffe salvate", { variant: "success" });
        closeDialog();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const getCosto = (costo: string) => {
    costo = costo.trim();
    return costo === "" ? null : parseFloat(costo);
  };

  const [tariffeState, setTariffeState] = useState<Tariffa[]>([]);
  const handleTariffaChange = (costo: string, associazione: number, uso: number) => {
    const newArr = [...tariffeState];
    let cur = tariffeState.find(
      (t) => t.tipologia_AssociazioniId === associazione && t.tipologia_UsoId === uso
    );
    if (cur === undefined)
      newArr.push({
        costoPerSlot: getCosto(costo),
        tipologia_AssociazioniId: associazione,
        tipologia_UsoId: uso,
      });
    else cur.costoPerSlot = getCosto(costo);

    setTariffeState(newArr);
  };
  const getTariffa = (associazione: number, uso: number) => {
    const ret = tariffeState.find(
      (t) => t.tipologia_AssociazioniId === associazione && t.tipologia_UsoId === uso
    )?.costoPerSlot;

    if (typeof ret === "number" && isNaN(ret)) return "";

    return ret;
  };

  const handleSave = () => {
    mutate({
      tariffe_DefinizioniId: data.id,
      tariffe: tariffeState.map((t) => {
        return {
          ...t,
          costoPerSlot: parseFloat("" + t.costoPerSlot),
        };
      }),
    });
  };

  useEffect(() => {
    refetch();
  }, [data.id, refetch]);

  if (isLoading || associazioniLoading || isLoadingUso) return <></>;

  return (
    <>
      <DialogTitle>
        {data.codice} - {data.nome}
      </DialogTitle>
      <DialogContent>
        <form>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                {uso?.tipologieUso.map((u) => (
                  <TableCell>{u.descrizioneUso}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {associazioni?.tipologie.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.denominazione}</TableCell>
                  {uso?.tipologieUso.map((u) => (
                    <TableCell>
                      <TextField
                        variant="standard"
                        type="number"
                        sx={{ maxWidth: "70px" }}
                        value={getTariffa(t.id, u.id)}
                        onChange={(event) => handleTariffaChange(event.target.value, t.id, u.id)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} autoFocus>
          Chiudi
        </Button>
        <Button onClick={handleSave} autoFocus>
          Salva
        </Button>
      </DialogActions>
    </>
  );
}
